import React from 'react';
import { FormattedMessage } from 'react-intl';
import Alert from '../../../_shared/components/Alert';
import { TradeState, TradeStateName } from '../../../_shared/types/trade';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';
import timeouts from '../../../_shared/constants/timeouts';
import UserService from '../../../services/UserService';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import { BidSource } from '../../../_shared/types/auction';
import ROUTES from '../../Routes/constants';
import {
  getSellerProperties,
  getVinBasicProperties
} from '../../../_shared/hooks/useAnalytics';
import SegmentConfig from '../../../_shared/constants/segmentConfig';
import TRToast from '../../../_shared/helpers/TRToast';

export const onErrorCallbackGeneric = () => {
  TRToast(
    <Alert
      variant="error"
      messages={[<FormattedMessage id="modal.negotiation.accept.error" />]}
    />
  );
};

const getUserType = (userService, user, tradeDetails, isSecondBidder) => {
  if (userService.isSeller(user, tradeDetails?.dealer?.id)) {
    return MixpanelConfig.userType.seller;
  }

  if (isSecondBidder) {
    return MixpanelConfig.userType.secondBidder;
  }

  return MixpanelConfig.userType.buyer;
};

export const declineOffer = ({
  setConfirmModalLogic,
  negotiationService,
  close,
  sellerLatestOffer,
  latestOffer,
  tradeDetails,
  setIsConfirmModalOpen,
  msgId,
  setIsLoading,
  isSellContext,
  user,
  isSecondBidder,
  onNegotiationEnded,
  track
}) => {
  const isVdp = window.location.pathname.startsWith(ROUTES.TRADE_URI);
  const userService = UserService.get();
  const eventSource = window.location.href.includes(ROUTES.TRADE_URI)
    ? BidSource.VDP_OVERLAY
    : BidSource.LIST_VIEW;

  setConfirmModalLogic([
    {
      msg: (
        <FormattedMessage
          id={msgId}
          values={{ askingPrice: convertToCurrency(sellerLatestOffer) }}
        />
      ),
      title: (
        <FormattedMessage
          id={
            isSellContext
              ? 'modal.negotiation.seller.decline.title'
              : 'modal.negotiation.buyer.decline.title'
          }
          values={{
            offer: convertToCurrency(latestOffer?.price)
          }}
        />
      ),
      confirmLabel: (
        <FormattedMessage
          id={
            isSellContext
              ? 'modal.negotiation.seller.decline.yesDecline'
              : 'modal.negotiation.buyer.decline.yesDecline'
          }
        />
      ),
      cancelLabel: <FormattedMessage id="modal.cancel" />,
      onCancel: () => () => {
        setIsConfirmModalOpen(false);
      },
      onConfirm: () => async () => {
        setIsLoading(true);
        negotiationService
          .rejectProposal(tradeDetails.auctionId)
          .then(response => {
            // Goes to lost unless there's a second bidder, where trade stays in pending.
            let toasterMsgId = 'modal.negotiation.decline.success';
            if (isSellContext && response?.tradeState === TradeState.pending) {
              toasterMsgId = 'modal.negotiation.decline.secondBidder.success';
            }

            TRToast(
              <Alert
                variant="success"
                messages={[<FormattedMessage id={toasterMsgId} />]}
              />
            );

            track(SegmentConfig.names.pendingDeclineOfferConfirmed, {
              ...getVinBasicProperties(tradeDetails),
              ...getSellerProperties(tradeDetails),
              event_state_type: userService.isSeller(
                user,
                tradeDetails?.dealer?.id
              )
                ? SegmentConfig.eventStateType.sellPending
                : SegmentConfig.eventStateType.buyPending
            });
            track(MixpanelConfig.names.declineOfferConfirm, {
              user_type: getUserType(
                userService,
                user,
                tradeDetails,
                isSecondBidder
              ),
              source: eventSource,
              trade_id: tradeDetails?.id,
              auction_id: tradeDetails?.auctionId
            });

            onNegotiationEnded(response?.tradeState);
          })
          .catch(onErrorCallbackGeneric)
          .finally(() => {
            close();
            setIsLoading(false);
          });

        if (isVdp) {
          setTimeout(() => {
            window.location.href = isSellContext
              ? `${ROUTES.SELLING_PENDING}`
              : `${ROUTES.BUYING_PENDING}`;
          }, 1000);
        }
      }
    }
  ]);
  track(SegmentConfig.names.pendingDeclineOfferClicked, {
    ...getVinBasicProperties(tradeDetails),
    ...getSellerProperties(tradeDetails),
    event_state_type: userService.isSeller(user, tradeDetails?.dealer?.id)
      ? SegmentConfig.eventStateType.sellPending
      : SegmentConfig.eventStateType.buyPending
  });
  setIsConfirmModalOpen(true);
  track(MixpanelConfig.names.declineOffer, {
    user_type: getUserType(userService, user, tradeDetails, isSecondBidder),
    source: eventSource,
    trade_id: tradeDetails?.id,
    auction_id: tradeDetails?.auctionId
  });
};

export const acceptOffer = ({
  setConfirmModalLogic,
  negotiationService,
  close,
  latestOffer,
  tradeDetails,
  setIsConfirmModalOpen,
  intl,
  setOnAccept,
  setIsLoading,
  user,
  isSecondBidder,
  onNegotiationEnded,
  track
}) => {
  const isVdp = window.location.pathname.startsWith(ROUTES.TRADE_URI);
  const userService = UserService.get();
  const eventSource = window.location.href.includes(ROUTES.TRADE_URI)
    ? BidSource.VDP_OVERLAY
    : BidSource.LIST_VIEW;

  setConfirmModalLogic([
    {
      msg: <FormattedMessage id="modal.negotiation.accept.description" />,
      title: (
        <FormattedMessage
          id="modal.negotiation.accept.title"
          values={{
            offer: convertToCurrency(latestOffer?.price)
          }}
        />
      ),
      confirmLabel: <FormattedMessage id="modal.negotiation.accept" />,
      cancelLabel: <FormattedMessage id="modal.cancel" />,
      onCancel: () => () => {
        setIsConfirmModalOpen(false);
      },
      onConfirm: () => async () => {
        setIsLoading(true);
        const onSuccessCallback = response => {
          setOnAccept(true);
          close();
          const tradeState = response?.tradeState;
          const stateLabel = intl.formatMessage({
            id: `tradeState.${TradeStateName[tradeState]?.toLowerCase()}`
          });
          TRToast(
            <Alert
              variant="success"
              messages={[
                <FormattedMessage
                  id="modal.negotiation.accepted"
                  values={{ state: stateLabel }}
                />
              ]}
            />,
            {
              autoClose: timeouts.INFO
            }
          );

          track(SegmentConfig.names.pendingAcceptOfferConfirmed, {
            ...getVinBasicProperties(tradeDetails),
            ...getSellerProperties(tradeDetails),
            event_state_type: userService.isSeller(
              user,
              tradeDetails?.dealer?.id
            )
              ? SegmentConfig.eventStateType.sellPending
              : SegmentConfig.eventStateType.buyPending
          });
          track(MixpanelConfig.names.acceptOfferConfirm, {
            user_type: getUserType(
              userService,
              user,
              tradeDetails,
              isSecondBidder
            ),
            source: eventSource,
            trade_id: tradeDetails?.id,
            auction_id: tradeDetails?.auctionId
          });
          onNegotiationEnded(response?.tradeState);
        };

        negotiationService
          .acceptProposal(tradeDetails.auctionId)
          .then(onSuccessCallback)
          .catch(onErrorCallbackGeneric)
          .finally(() => {
            close();
            setIsLoading(false);
          });

        if (isVdp) {
          setTimeout(() => window.location.reload(), 1000);
        }
      }
    }
  ]);
  track(SegmentConfig.names.pendingAcceptOfferClicked, {
    ...getVinBasicProperties(tradeDetails),
    ...getSellerProperties(tradeDetails),
    event_state_type: userService.isSeller(user, tradeDetails?.dealer?.id)
      ? SegmentConfig.eventStateType.sellPending
      : SegmentConfig.eventStateType.buyPending
  });
  setIsConfirmModalOpen(true);
  track(MixpanelConfig.names.acceptOffer, {
    user_type: getUserType(userService, user, tradeDetails, isSecondBidder),
    source: eventSource,
    trade_id: tradeDetails?.id,
    auction_id: tradeDetails?.auctionId
  });
};
